import classNames from 'classnames';
import { object, string } from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { SubmitHandler, useForm } from 'react-hook-form';
import {
  ValidPhoneNumber,
  ValidEmailAddress,
} from '@javascript/lib/regularExpressions';

const formSchema = object().shape({
  contactEmail: string()
    .matches(ValidEmailAddress, 'Please enter your email address')
    .required('Please enter your email address'),
  contactNumber: string()
    .matches(ValidPhoneNumber, 'Please enter your phone number')
    .required('Please enter your phone number'),
});

interface FormInput {
  contactEmail: string;
  contactNumber: string;
}

interface ContactViewProps {
  customerDetails: { contact_email: string; contact_number: string };
  nextView: () => void;
  updateCustomerDetails: (details: {
    contact_email: string;
    contact_number: string;
  }) => void;
}

const ContactView = ({
  customerDetails,
  nextView,
  updateCustomerDetails,
}: ContactViewProps): JSX.Element => {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(formSchema),
  });

  const onSubmit: SubmitHandler<FormInput> = (data) => {
    updateCustomerDetails({
      contact_email: data.contactEmail,
      contact_number: data.contactNumber,
    });
    nextView();
  };

  return (
    <div className="db-modal__content">
      <form onSubmit={handleSubmit(onSubmit)}>
        <section className="db-section ac-animation--fade-in-from-right">
          <p className="db-question ch-mb--2 ch-fs--5 ch-fw--500">
            Your contact details
          </p>
          <p className="ch-color--grey-6 ch-fs--3 ch-mb--4">
            We&apos;ll take your contact details in case we need to get in touch
            about your reservation
          </p>
          <div
            className={classNames(
              'ch-form__group',
              errors.contactEmail && 'ch-form__group--error',
            )}
          >
            <label className="ch-label" htmlFor="contactEmail">
              Email address
            </label>
            <p className="ch-form__control-hint">For your confirmation email</p>
            <span className="ch-form__control-validation">
              {errors.contactEmail?.message}
            </span>
            <input
              className="ch-form__control ch-mb--3"
              defaultValue={customerDetails.contact_email}
              id="contactEmail"
              type="text"
              {...register('contactEmail')}
            />
          </div>
          <div
            className={classNames(
              'ch-form__group',
              errors.contactNumber && 'ch-form__group--error',
            )}
          >
            <label className="ch-label" htmlFor="contactNumber">
              Phone number
            </label>
            <p className="ch-form__control-hint">
              We&apos;ll only call you to talk about your reservation
            </p>
            <span className="ch-form__control-validation">
              {errors.contactNumber?.message}
            </span>
            <input
              className="ch-form__control ch-mb--3"
              defaultValue={customerDetails.contact_number}
              id="contactNumber"
              type="text"
              {...register('contactNumber')}
            />
          </div>
        </section>
        <section className="db-section db-section--cta">
          <button className="ch-btn ch-btn--block ch-btn--cta" type="submit">
            Next: Additional information
          </button>
        </section>
      </form>
    </div>
  );
};

export default ContactView;
